/**
 * Marks that this tab started a Google sign-in, and where to go afterwards.
 *
 * The backend returns from Google to `/?google_code=…`. OAuthTokenHandler exchanges that code
 * only while this marker exists, so a link with someone else's code cannot sign a visitor into
 * the wrong account (login CSRF on the last leg; the backend's `state` covers the Google leg).
 * sessionStorage is per tab and survives the round trip to Google. Storage can be blocked:
 * every access is guarded, and then the Google return is simply ignored.
 */
const KEY = 'lukaaji.oauthPending';

/** Same as the backend's OAuth state TTL (10 minutes). */
export const OAUTH_PENDING_TTL_MS = 10 * 60 * 1000;

interface PendingSignIn {
  redirect: string | null;
}

export function markOAuthPending(redirect: string | null, now: number = Date.now()): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ at: now, redirect }));
  } catch {
    // Storage blocked: the Google return will be ignored.
  }
}

export function clearOAuthPending(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored.
  }
}

/** The pending sign-in, once (the marker is removed); null if none, expired or malformed. */
export function takeOAuthPending(now: number = Date.now()): PendingSignIn | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  clearOAuthPending();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { at, redirect } = parsed as { at?: unknown; redirect?: unknown };
  if (typeof at !== 'number' || now - at > OAUTH_PENDING_TTL_MS || now < at) return null;
  if (redirect !== null && typeof redirect !== 'string') return null;
  return { redirect };
}
